'use client';

import { useState } from 'react';
import { useV4Enter } from '@/hooks/useV4Enter';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import {
  SectLabel,
  SectH2,
  SectLede,
  GlassCard,
  V4CtaButton,
  V4GhostButton,
} from '@/components/landing/v4-shared';
import { MagneticCta } from '@/components/marketing/MagneticCta';
import { PartnersSignupForm } from '@/components/partners/PartnersSignupForm';
import { PARTNERS_COPY } from '@/lib/marketing/messaging-bank';
import { wrapGraph, buildBreadcrumbSchema, buildWebPageSchema } from '@/lib/seo/schema-builders';

const TIERS = [
  { count: '10 referrals', payout: '$500/month passive income' },
  { count: '25 referrals', payout: '$1,250/month passive income' },
  { count: '50 referrals', payout: '$2,500/month passive income' },
];

const ADVISOR_TYPES = [
  {
    title: 'CPAs and Accountants',
    body: 'You see their revenue every quarter. Refer them to BizRnR and help them capture every lead.',
  },
  {
    title: 'Insurance Brokers',
    body: 'Your clients are service businesses. They need every call answered. BizRnR makes sure they do.',
  },
  {
    title: 'Business Consultants',
    body: 'Phone automation is the top ROI recommendation for service businesses. Add BizRnR to your toolkit.',
  },
];

const FLOW_STEPS = [
  { title: 'Apply', body: 'Fill out the form. We review within 24 hours.' },
  { title: 'Get your link', body: 'Unique referral link and co-branded materials.' },
  { title: 'Refer clients', body: 'Share your link. We handle onboarding and support.' },
  { title: 'Earn monthly', body: '$50/month per active referral. No cap.' },
];

export default function PartnersPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  // Wave 6.B: scroll-fade reveals on advisor + flow grids
  const advisorRef = useV4Enter<HTMLDivElement>();
  const flowRef = useV4Enter<HTMLDivElement>();

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    const formData = new FormData(e.currentTarget);
    const body: Record<string, string> = {};
    formData.forEach((value, key) => {
      body[key] = value.toString();
    });

    try {
      const res = await fetch('/api/partners/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Request failed (${res.status})`);
      }

      setSubmitStatus('success');
    } catch (err) {
      setSubmitStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const jsonLd = wrapGraph([
    buildWebPageSchema({
      path: '/partners',
      name: PARTNERS_COPY.metadata.title,
      description: PARTNERS_COPY.metadata.description,
      lastModified: '2026-07-17',
    }),
    buildBreadcrumbSchema({
      path: '/partners',
      items: [
        { name: 'Home', url: 'https://bizrnr.com' },
        { name: 'Partners', url: 'https://bizrnr.com/partners' },
      ],
    }),
  ]);

  return (
    <main
      data-v4-root
      data-pillar="paper"
      style={{ background: 'var(--ink)', color: 'var(--paper)' }}
      className="relative"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-20">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <SectLabel>{PARTNERS_COPY.eyebrow}</SectLabel>
              <h1
                style={{
                  fontFamily: 'var(--font-geist-sans)',
                  fontWeight: 500,
                  fontSize: 'clamp(40px, 7vw, 96px)',
                  letterSpacing: '-0.035em',
                  lineHeight: 1.02,
                  color: 'var(--paper)',
                  maxWidth: '18ch',
                  margin: '0 0 24px',
                }}
              >
                Your clients are losing calls.{' '}
                <em
                  style={{
                    fontFamily: 'var(--font-instrument), serif',
                    fontStyle: 'italic',
                    fontWeight: 400,
                    color: 'var(--paper)',
                    letterSpacing: '-0.015em',
                  }}
                >
                  Help them — earn $50/mo.
                </em>
              </h1>
              <h2 style={{ fontSize: 20, color: 'var(--paper)', margin: '0 0 8px' }}>
                How does the BizRnR partner program work?
              </h2>
              <SectLede className="speakable-answer">{PARTNERS_COPY.lede}</SectLede>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <MagneticCta>
                  <V4CtaButton href="#partner-signup" size="lg">
                    {PARTNERS_COPY.ctaPrimary.label}
                  </V4CtaButton>
                </MagneticCta>
                <V4GhostButton variant="paper" href="#how-it-works" size="lg">
                  How it works
                </V4GhostButton>
              </div>
            </div>

            <GlassCard accent="cyan" padding={32} radius={24}>
              <div
                style={{
                  fontFamily: 'var(--font-geist-sans)',
                  fontSize: 56,
                  fontWeight: 600,
                  color: 'var(--cyan-glow)',
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                  marginBottom: 8,
                }}
              >
                $50
              </div>
              <p
                style={{
                  fontFamily: 'var(--font-geist-sans)',
                  fontSize: 17,
                  color: 'var(--dim)',
                  margin: '0 0 24px',
                }}
              >
                per referral, every month, recurring
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 16 }}>
                {TIERS.map((t, idx) => (
                  <li key={t.count} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <span
                      aria-hidden="true"
                      style={{
                        flex: '0 0 28px',
                        height: 28,
                        borderRadius: 999,
                        background: 'rgba(125,250,255,0.18)',
                        color: 'var(--cyan-glow)',
                        fontFamily: 'var(--font-geist-mono)',
                        fontSize: 12,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {idx + 1}
                    </span>
                    <span>
                      <span
                        style={{
                          display: 'block',
                          fontWeight: 500,
                          color: 'var(--paper)',
                          fontFamily: 'var(--font-geist-sans)',
                        }}
                      >
                        {t.count}
                      </span>
                      <span style={{ color: 'var(--dim)', fontSize: 14 }}>{t.payout}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Advisors section */}
      <section className="py-20 border-t" style={{ borderColor: 'var(--hair)' }}>
        <div className="max-w-[1200px] mx-auto px-6">
          <SectLabel>Advisors</SectLabel>
          <SectH2>{PARTNERS_COPY.sections[0].heading}</SectH2>
          <div ref={advisorRef} className="grid md:grid-cols-3 gap-6 mt-10 v4-enter v4-stagger">
            {ADVISOR_TYPES.map((a) => (
              <GlassCard key={a.title} accent="cyan" hoverable>
                <h3
                  style={{
                    fontFamily: 'var(--font-geist-sans)',
                    fontSize: 18,
                    fontWeight: 500,
                    color: 'var(--paper)',
                    margin: '0 0 8px',
                  }}
                >
                  {a.title}
                </h3>
                <p style={{ color: 'var(--dim)', fontSize: 14, lineHeight: 1.55, margin: 0 }}>
                  {a.body}
                </p>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20 border-t" style={{ borderColor: 'var(--hair)' }}>
        <div className="max-w-[1200px] mx-auto px-6">
          <SectLabel>Process</SectLabel>
          <SectH2>{PARTNERS_COPY.sections[1].heading}</SectH2>
          <div ref={flowRef} className="grid md:grid-cols-4 gap-8 mt-10 v4-enter v4-stagger">
            {FLOW_STEPS.map((s, idx) => (
              <div key={s.title}>
                <div
                  style={{
                    fontFamily: 'var(--font-geist-mono)',
                    fontSize: 32,
                    fontWeight: 500,
                    color: 'var(--cyan-glow)',
                    opacity: 0.45,
                    margin: '0 0 12px',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {String(idx + 1).padStart(2, '0')}
                </div>
                <h3
                  style={{
                    fontFamily: 'var(--font-geist-sans)',
                    fontSize: 18,
                    fontWeight: 500,
                    color: 'var(--paper)',
                    margin: '0 0 6px',
                  }}
                >
                  {s.title}
                </h3>
                <p style={{ color: 'var(--dim)', fontSize: 14, lineHeight: 1.55, margin: 0 }}>
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Apply form */}
      <section
        id="partner-signup"
        className="py-20 border-t"
        style={{ borderColor: 'var(--hair)' }}
      >
        <div className="max-w-[640px] mx-auto px-6">
          <SectLabel>Apply</SectLabel>
          <SectH2 style={{ textAlign: 'center', marginInline: 'auto' }}>
            {PARTNERS_COPY.sections[2].heading}
          </SectH2>
          <PartnersSignupForm
            onSubmit={handleSubmit}
            submitting={submitting}
            submitStatus={submitStatus}
            errorMessage={errorMessage}
          />
        </div>
      </section>

      <Footer />
    </main>
  );
}
